import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KeyRound, Printer, RefreshCw, ShieldCheck, Mail, Check, AlertTriangle, Lock } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { hardwareApi, type DeviceUnitRow, type DeviceModelRow } from '../../services/hardwareApi';
import { sendUnlockCodeEmails } from './welcomeUnlockEmail';
import LockReceiptList from './LockReceiptList';
import { ForLocation, registersForUnits } from './DeviceSetupPanels';
import AccessVerifyTabs from './AccessVerifyTabs';
import { getAdminProductConfig } from '../../config/productConfig';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';
import { usableTerminalCode } from '../../components/pos/TerminalNewCodeModal';

/**
 * HAND THE TILL OVER — the step between "we have proved this works" and
 * "seal the box".
 *
 * Everything before this proved the device works on OUR account. Here it stops
 * being ours: the owner gets a full-access role and a PIN, our test session is
 * cleared off the device, and the two codes go out by email and on the card
 * that goes in the box.
 *
 * DEREGISTER, NOT RELEASE. `release` kills the provisioning code permanently
 * and ends the terminal; `deregister` flips it back to `pending` so the SAME
 * code can be typed in by the customer — and because the terminalId does not
 * change, the register survives with every payment type, tip setting and
 * button layout configured during testing already on it. Releasing here would
 * hand the customer a fresh, empty terminal and throw the testing away.
 *
 * LOCK FOR SHIPPING ("Hello from Ruby", 2026-10-31) — on by default. The tills
 * are NOT deregistered: they ship paired, Fresh and Locked, and the owner gets
 * ONE email holding the order's unlock code and their PIN. Nothing secret goes
 * in the box — the card only says to check the email. Off = Ship unlocked,
 * which is exactly the deregister + device-code flow above, unchanged.
 */

interface Props {
  tenantId: string;
  tenantName?: string;
  locationId?: string;
  locationName?: string;
  /** The owner's platform user — who gets the role and the PIN. */
  ownerUserId?: string;
  ownerEmail?: string;
  ownerName?: string;
  orderNumber?: string;
  /** The device order — needed for Lock for shipping (its unlock code is per order). */
  orderId?: string;
  /** The order's derived status — an order packed or later with no lock stays on the old flow. */
  orderStatus?: string;
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  posUnitIds: readonly string[];
  /** Provisioned terminals for this tenant, so we know what to deregister. */
  terminals: any[] | null;
  onDone: () => void;
}

interface Issued {
  pin: string;
  pinViewableLater: boolean;
  roleName: string;
  roleCreated: boolean;
  resolvedPosButtons: string[];
  provisionCodes: { code: string; registerName?: string }[];
  emailedTo?: string;
  locationAccess?: { role: string; hourlyRate: number; created: boolean } | null;
  at: number;
  /** Set when this run took the Lock for shipping path. */
  locked?: { emailed: boolean; unpaired: string[]; uncoupled?: string[] };
}

/**
 * EVERY MENU THE PRODUCT HAS, flattened out of the sidebar registry.
 *
 * The owner's role is meant to grant the lot, and the merchant's role editor
 * reads the stored `platformMenuAccess` array rather than the admin wildcard —
 * so an empty array is an "Online" tab with nothing ticked, on the role that is
 * supposed to have everything. The registry is per-product and editable here,
 * which is why this app sends the list rather than pos-auth guessing at it.
 *
 * ROUTES, NOT IDS. `platformMenuAccess` is keyed on `item.route` — every
 * toggle, count and sub-tab check in the merchant's MenuAccessTree tests
 * `allowedSet.has(item.route)`, and `canAccessTab` prefix-matches a granted
 * parent route to cover its tabs. Sending `item.id` writes 41 entries that
 * match nothing: the role reads as fully granted in the database and as
 * nothing at all on screen.
 *
 * Subgroups nest, so this recurses; routes are de-duplicated because a menu
 * item may legitimately appear under more than one group.
 */
const collectMenuIds = (groups: any[]): string[] => {
  const routes = new Set<string>();
  const walk = (list: any[]) => {
    for (const g of list || []) {
      for (const item of g?.items || []) if (item?.route) routes.add(item.route);
      if (Array.isArray(g?.subgroups)) walk(g.subgroups);
    }
  };
  walk(groups);
  return [...routes];
};

/** Order statuses at or past packing — with no lock, such an order went out on the RT- code flow. */
const PACKED_OR_LATER = new Set(['packing', 'shipped', 'delivered', 'deployed']);

const TillHandoverPanel: React.FC<Props> = ({
  tenantId, tenantName, locationId, locationName,
  ownerUserId, ownerEmail, ownerName, orderNumber, orderId, orderStatus,
  units, models, posUnitIds, terminals, onDone,
}) => {
  /* Lock for shipping — on by default; needs the order, since the code is per order. */
  const [lockForShipping, setLockForShipping] = useState(true);
  const lockPath = lockForShipping && !!orderId;
  /** The order already carries a shipping lock — a restored handover prints the locked card. */
  const [orderLocked, setOrderLocked] = useState(false);
  const [orderLockEmailed, setOrderLockEmailed] = useState(false);
  const switchSeeded = useRef(false);
  /** The last Lock for shipping attempt failed — the box card is not printable until a run succeeds. */
  const [lockFailed, setLockFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [showAccess, setShowAccess] = useState(false);
  /* `null` until the first read finishes, so a refresh never flashes the
     "hand over" button at an order that has already been handed over — that is
     how an operator ends up issuing a second PIN over a printed card. */
  const [loaded, setLoaded] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const dataVersion = useDataVersion();

  /** The tills on THIS order, and the provisioning codes they were given.
   *  Matched on `deviceUnitId` — a register renamed "Front counter" is still
   *  this order's till. The name match is only for unlinked legacy registers. */
  const mine = useMemo(() => {
    const posUnits = units.filter((u) => posUnitIds.includes(u.unitId));
    return registersForUnits(terminals, posUnits);
  }, [terminals, units, posUnitIds]);

  const deviceLabel = useMemo(() => {
    const u = units.find((x) => posUnitIds.includes(x.unitId));
    return models.find((m) => m.modelId === u?.modelId)?.modelName;
  }, [units, models, posUnitIds]);

  /**
   * RESTORE, rather than re-issue.
   *
   * The PIN lived only in this component's state, so a refresh lost it and the
   * only route back was the button — which mints a NEW pin and silently
   * invalidates the one already printed on a card and emailed. It is stored
   * reversibly for exactly this reason, so it is read back.
   */
  const load = useCallback(async () => {
    if (!ownerUserId && !ownerEmail) { setLoaded(true); return; }
    /* THE SWITCH DEFAULT COMES FROM THE ORDER, never from the tenant's handover
       record (which is per owner, not per order):
         - the order has a lock      → its own path: !shipUnlocked
         - no lock, packing or later → it went out the old way: OFF
         - otherwise                 → ON (even if this owner had an earlier handover)
       A failed read never seeds OFF — it stays ON with a quiet warning. */
    const lockRead = orderId
      ? hardwareApi.getOrderWelcome(orderId)
        .then((w) => ({ ok: true, lock: w.lock }))
        .catch(() => ({ ok: false, lock: null as null | { shipUnlocked: boolean; emailedAt?: string } }))
      : Promise.resolve({ ok: true, lock: null });
    try {
      const [{ handover }, read] = await Promise.all([
        api.posAuth.getHandover(tenantId, { userId: ownerUserId, ownerEmail }),
        lockRead,
      ]);
      const locked = !!read.lock && !read.lock.shipUnlocked;
      setOrderLocked(locked);
      setOrderLockEmailed(!!read.lock?.emailedAt);
      if (!switchSeeded.current) {
        switchSeeded.current = true;
        if (!read.ok) {
          toastService.warning('Could not read this order\'s shipping lock — Lock for shipping stays on.');
        } else if (read.lock) {
          setLockForShipping(!read.lock.shipUnlocked);
        } else if (PACKED_OR_LATER.has(String(orderStatus || ''))) {
          setLockForShipping(false);
        }
      }
      if (handover) {
        // A re-read keeps what only this session knows (a PIN shown once, the
        // codes, who it was emailed to) — only the stored fields are replaced.
        setIssued((prev) => ({
          ...prev,
          pin: handover.pin || prev?.pin || '',
          pinViewableLater: handover.pinViewableLater,
          roleName: handover.role.name || 'Full Admin Access',
          roleCreated: prev?.roleCreated ?? false,
          resolvedPosButtons: handover.role.resolvedPosButtons || [],
          // Read from the terminals this order carries, same as after issuing.
          provisionCodes: prev?.provisionCodes ?? [],
          at: handover.at,
        }));
      }
    } catch { /* leaves the panel offering the button, which is the safe default */ }
    finally { setLoaded(true); }
  }, [tenantId, ownerUserId, ownerEmail, orderId, orderStatus]);

  // dataVersion: re-read after any save on the order page
  useEffect(() => { load(); }, [load, dataVersion]);

  /* The codes come off the order's own terminals, so a restored handover shows
     the same card contents as a fresh one without storing them twice. */
  const codesFromOrder = useMemo(
    () => mine.filter((t) => usableTerminalCode(t))
      .map((t) => ({ code: usableTerminalCode(t) as string, registerName: t.registerName })),
    [mine],
  );

  /**
   * `reissuePin` is the ONLY destructive part of this, and it is opt-in.
   *
   * Without it every step re-does itself and changes nothing that was already
   * right — same role, same user, same location grant, same PIN. That is what
   * makes it safe to press per-till on an order with several machines, and
   * what makes "re-run it and check" a thing an operator can do rather than a
   * thing that reprints a card.
   */
  const run = async ({ reissuePin = false }: { reissuePin?: boolean } = {}) => {
    const lock = lockPath;
    setLockFailed(false);
    if (!ownerUserId && !ownerEmail) {
      toastService.error('This tenant has no owner on file — set one before handing the till over.');
      return;
    }
    setBusy(true);
    try {
      /* 0. The menus the role should grant. Best-effort: a registry that will
            not load must not cost the operator the handover — the role's admin
            wildcards still hold, so this widens what the merchant SEES on the
            role rather than what they can do. */
      let platformMenuAccess: string[] = [];
      try {
        const { sidebarConfig } = await api.sidebar.get(getAdminProductConfig().id);
        platformMenuAccess = collectMenuIds(sidebarConfig?.groups || []);
      } catch {
        toastService.warning('Could not read the menu list — the owner keeps full admin access, but tick the Online tab on their role by hand.');
      }

      /* 1. The role, the PIN, and access to this location — all idempotent.
         The role is found by name and topped up, the location grant is left
         alone if it is already there, and the PIN comes back unchanged unless
         a re-issue was explicitly asked for. */
      const res = await api.posAuth.handover(tenantId, {
        userId: ownerUserId, ownerEmail, locationId, platformMenuAccess, reissuePin,
      });
      let emailedTo: string | undefined;

      if (lock && orderId) {
        /* 2. LOCK FOR SHIPPING instead of deregistering. One code per order;
              issue is idempotent, so a re-run returns the SAME code. Units with
              no paired register cannot be locked — they are named, left alone
              (not deregistered), and ship on the old flow. */
        let lockedResult: Issued['locked'] = { emailed: false, unpaired: [] };
        try {
          const lockRes = await hardwareApi.issueOrderWelcome(orderId, { shipUnlocked: false });
          const unpaired = lockRes.unpaired || [];
          const uncoupled = lockRes.uncoupled || [];
          lockedResult = { emailed: false, unpaired, uncoupled };
          if (unpaired.length) {
            toastService.warning(`${unpaired.length} device${unpaired.length === 1 ? ' has' : 's have'} no paired register and ship on the old flow: ${unpaired.map(unitName).join(', ')}`);
          }
          if (uncoupled.length) {
            toastService.warning(`${uncoupled.length} pay screen${uncoupled.length === 1 ? ' has' : 's have'} no till in this order, so ${uncoupled.length === 1 ? 'it ships' : 'they ship'} unlocked: ${uncoupled.map(unitName).join(', ')}`);
          }

          /* 3. ONE email, both secrets: the unlock code and the PIN. After the
                credential is written, never before. */
          const to = ownerEmail || lockRes.ownerEmail;
          if (!to) {
            toastService.warning('The devices are locked but there is no owner email to send the unlock code and PIN to — set one, then use Resend code on the terminal\'s Control tab.');
          } else {
            try {
              const { stamped } = await sendUnlockCodeEmails({
                orderId, to, code: lockRes.code, pin: res.pin,
                ownerName, businessName: tenantName, locationName, orderNumber,
                devices: lockRes.devices.map((d) => ({ label: d.label, registerName: d.registerName })),
              });
              lockedResult.emailed = true;
              emailedTo = to;
              if (!stamped) toastService.warning('The email went out, but the order did not record it — the Control tab may show "not emailed".');
            } catch {
              toastService.warning('The devices are locked but the unlock email did not send — use Resend code on the terminal\'s Control tab.');
            }
          }
          setOrderLocked(true);
          setLockFailed(false);
        } catch (e: any) {
          toastService.error(`Could not lock the order for shipping (${e?.message || 'error'}). The PIN is issued — press Re-run setup to retry, or switch to Ship unlocked first.`);
          lockedResult = undefined;
          setLockFailed(true);
        }
        setIssued({
          pin: res.pin,
          pinViewableLater: res.pinViewableLater,
          roleName: res.role.name,
          roleCreated: res.role.created,
          resolvedPosButtons: res.role.resolvedPosButtons || [],
          provisionCodes: [],
          emailedTo,
          locationAccess: res.locationAccess,
          at: res.at,
          locked: lockedResult,
        });
        // Success is claimed only when the owner's email was actually accepted.
        if (lockedResult?.emailed) toastService.success('Locked for shipping — the owner has one email with the unlock code and PIN. Print the box card.');
        onDone();
        return;
      }

      /* 2. Clear OUR session off the devices. Best-effort per terminal: one
            that will not deregister must not cost the operator the PIN that
            has already been issued — it is reported and the rest continue. */
      const codes: { code: string; registerName?: string }[] = [];
      for (const t of mine) {
        /* The list masks an activated till's code (RT-****-AB12); deregister
           re-arms it and returns it in full — that is the one to print/email. */
        let code = usableTerminalCode(t);
        try {
          const resp: any = await api.posTerminals.deregister(t.terminalId, tenantId);
          code = usableTerminalCode({ provisionCode: resp?.provisionCode }) || code;
        } catch (e: any) {
          toastService.warning(`${t.registerName || t.terminalId} did not deregister — clear it by hand before packing.`);
        }
        if (code) codes.push({ code, registerName: t.registerName });
      }

      /* 3. The email. After the credential is written, never before — an email
            promising a PIN that failed to save is worse than no email. */
      if (ownerEmail) {
        try {
          await api.email.send({
            to: [ownerEmail],
            templateId: 'pos-handover-owner',
            templateData: {
              ownerName: ownerName || '',
              businessName: tenantName || '',
              locationName: locationName || '',
              provisionCode: codes[0]?.code || '',
              pin: res.pin,
              deviceLabel: deviceLabel || '',
              orderNumber: orderNumber || '',
              loginUrl: window.location.origin.includes('localhost')
                ? 'https://dev.rubytill.com' : 'https://rubytill.com',
            },
          });
          emailedTo = ownerEmail;
        } catch (e: any) {
          toastService.warning('The codes were issued but the email did not send — print the card instead.');
        }
      }

      setIssued({
        pin: res.pin,
        pinViewableLater: res.pinViewableLater,
        roleName: res.role.name,
        roleCreated: res.role.created,
        resolvedPosButtons: res.role.resolvedPosButtons || [],
        provisionCodes: codes,
        emailedTo,
        locationAccess: res.locationAccess,
        at: res.at,
      });
      toastService.success(res.pinReused
        ? 'Setup re-run — role, location access and device session all refreshed. The PIN is unchanged, so the printed card is still correct.'
        : 'Till handed over. The PIN is on screen — print the card before you close this.');
      onDone();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not hand the till over.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * The card that goes in the box.
   *
   * Printed from a detached window rather than the app's own DOM: the order
   * screen's stylesheet would otherwise decide what a printed card looks like,
   * and this has to be legible in a warehouse, not pretty in a browser.
   */
  const printCard = () => {
    if (!issued) return;
    const w = window.open('', '_blank', 'width=840,height=1100');
    if (!w) { toastService.error('Allow pop-ups to print the card.'); return; }
    const esc = (v?: string) => String(v || '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string));
    /* LOCKED FOR SHIPPING: nothing secret in the box — no code, no PIN, no RT- code.
       The owner's one email carries both. */
    if (issued.locked || orderLocked) {
      w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Hello from Ruby — ${esc(tenantName)}</title>
<style>
  @page { size: A5; margin: 14mm; }
  body { font-family: Arial, Helvetica, sans-serif; color:#0A0A0D; margin:0; text-align:center; }
  .gem { margin:28mm auto 8mm; width:44px; height:44px; background:#DC143C; transform:rotate(45deg); border-radius:6px; }
  h1 { font-size:26px; margin:0 0 10px; }
  .msg { font-size:16px; color:#3f3f46; line-height:1.55; margin:0 auto; max-width:110mm; }
  .sub { font-size:12px; color:#78716c; margin:14mm 0 0; }
</style></head><body>
  <div class="gem"></div>
  <h1>Hello from Ruby</h1>
  <p class="msg">Check your email for your unlock code.</p>
  <p class="sub">${esc([tenantName, locationName].filter(Boolean).join(' · '))}${orderNumber ? ` &middot; Order ${esc(orderNumber)}` : ''}</p>
  <script>window.onload = function(){ window.print(); }<\/script>
</body></html>`);
      w.document.close();
      return;
    }
    const code = (issued.provisionCodes[0] || codesFromOrder[0])?.code || '';
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Till setup — ${esc(tenantName)}</title>
<style>
  @page { size: A5; margin: 14mm; }
  body { font-family: Arial, Helvetica, sans-serif; color:#292524; margin:0; }
  h1 { font-size:20px; margin:0 0 2px; color:#DC143C; }
  .sub { font-size:12px; color:#78716c; margin:0 0 18px; }
  .step { border:1px solid #d6d3d1; border-radius:8px; margin:0 0 12px; overflow:hidden; }
  .cap { background:#fafaf9; padding:7px 12px; font-size:10px; letter-spacing:.12em; text-transform:uppercase; color:#78716c; font-weight:bold; }
  .val { padding:14px 12px; text-align:center; font-family:'Courier New',monospace; font-size:30px; font-weight:bold; letter-spacing:.2em; }
  .note { padding:0 12px 11px; font-size:11px; color:#57534e; line-height:1.55; }
  .warn { border-left:3px solid #f59e0b; background:#fffbeb; padding:10px 12px; font-size:11px; line-height:1.6; color:#78350f; border-radius:0 6px 6px 0; }
  .foot { margin-top:16px; font-size:10px; color:#a8a29e; }
</style></head><body>
  <h1>Your till is ready</h1>
  <p class="sub">${esc([tenantName, locationName].filter(Boolean).join(' · '))}${orderNumber ? ` &middot; Order ${esc(orderNumber)}` : ''}</p>
  <div class="step"><div class="cap">Step 1 &middot; Device code</div><div class="val">${esc(code)}</div>
    <div class="note">Type this on the till when it asks to be set up. Everything we configured is already on it.</div></div>
  <div class="step"><div class="cap">Step 2 &middot; Your PIN</div><div class="val">${esc(issued.pin)}</div>
    <div class="note">Your sign-in PIN for the till. Full access to everything on your plan.</div></div>
  <div class="warn"><b>Change your PIN once you are signed in.</b> Anyone holding this card can open your till until you do.</div>
  <p class="foot">RubyTill &middot; issued ${new Date(issued.at).toLocaleString()}</p>
  <script>window.onload = function(){ window.print(); }<\/script>
</body></html>`);
    w.document.close();
  };

  /** "T3 Pro (SN …)" for a unit id the server named — falls back to the id. */
  const unitName = (id: string) => {
    const u = units.find((x) => x.unitId === id);
    const m = models.find((x) => x.modelId === u?.modelId)?.modelName;
    return m ? `${m} (${u?.serialNumber || id})` : id;
  };

  /* LOCK FOR SHIPPING — a custom switch (never a native checkbox), on by default. */
  const lockSwitch = orderId ? (
    <button
      type="button"
      role="switch"
      aria-checked={lockForShipping}
      onClick={() => setLockForShipping((v) => !v)}
      disabled={busy || orderLocked}
      title={orderLocked ? 'This order is locked for shipping — use the Control tab to unlock a device' : undefined}
      className="inline-flex min-h-[44px] items-center gap-2.5 rounded-lg px-1 text-[12.5px] font-semibold text-stone-800 disabled:opacity-50"
    >
      <span className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${lockForShipping ? 'bg-stone-950' : 'bg-stone-300'}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${lockForShipping ? 'left-[18px]' : 'left-0.5'}`} />
      </span>
      {lockForShipping ? 'Lock for shipping' : 'Ship unlocked'}
    </button>
  ) : <span />;

  if (!locationId) {
    return <p className="text-[12.5px] text-stone-500">This order has no location, so there is no till to hand over.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <ForLocation tenantName={tenantName} locationName={locationName} locationId={locationId} what="Till access" />

      {!loaded ? (
        <RubyLoader variant="section" />
      ) : !issued ? (
        <>
          <div className="rounded-xl border border-stone-200 bg-white p-3.5 text-[12.5px] leading-relaxed text-stone-600">
            <p className="mb-2 font-semibold text-stone-800">This hands the till to the merchant.</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Gives <strong>{ownerEmail || 'the owner'}</strong> the <strong>Full Admin Access</strong> role — every POS button and every Online menu</li>
              <li>Turns on their access to <strong>{locationName || 'this location'}</strong> as <strong>Owner</strong>, at <strong>$15/hr</strong> for them to change</li>
              <li>Issues them a new <strong>6-digit PIN</strong>, shown once, here</li>
              {lockPath ? (
                <>
                  <li>Locks every device on this order for shipping — they stay paired, Fresh, with everything configured during testing</li>
                  <li>Emails the owner one unlock code and their PIN, and gives you a box card with nothing secret on it</li>
                </>
              ) : (
                <>
                  <li>Clears our test session off {mine.length === 1 ? 'the till' : `${mine.length} tills`} — the same device code still works, and everything configured during testing stays on it</li>
                  <li>Emails the codes and gives you a card to print for the box</li>
                </>
              )}
            </ul>
            {!ownerUserId && !ownerEmail && (
              <p className="mt-2.5 flex items-start gap-1.5 text-amber-800">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                No owner on this tenant — set one before handing over.
              </p>
            )}
            {!lockPath && mine.length === 0 && (
              <p className="mt-2.5 flex items-start gap-1.5 text-amber-800">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                No provisioned till found for this order. The access and PIN will still be issued, but there is no device code to print.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {lockSwitch}
            <button
              onClick={() => run()}
              disabled={busy || (!ownerUserId && !ownerEmail)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-rose-600 px-4 text-[12.5px] font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <KeyRound size={13} />}
              Hand the till over
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="mb-3 flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-800">
              <Check size={14} /> Handed over{(issued.locked || orderLocked) ? ', locked for shipping' : ''}{issued.emailedTo ? ` — emailed to ${issued.emailedTo}` : ' — not emailed'}
            </p>
            {issued.locked && issued.locked.unpaired.length > 0 && (
              <p className="mb-3 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-amber-800">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                No paired register, so not locked — these ship on the old flow: {issued.locked.unpaired.map(unitName).join(', ')}
              </p>
            )}
            {issued.locked && (issued.locked.uncoupled?.length ?? 0) > 0 && (
              <p className="mb-3 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-amber-800">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                No till in this order to unlock with, so not locked — these pay screens ship unlocked: {issued.locked.uncoupled!.map(unitName).join(', ')}
              </p>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {(issued.locked || orderLocked) ? (
                <div className="rounded-lg border border-stone-200 bg-white p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-400">Unlock code</p>
                  <p className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-stone-900">
                    <Lock size={13} /> {(issued.locked?.emailed || orderLockEmailed) ? 'Emailed to the owner' : 'Not emailed yet'}
                  </p>
                  <p className="mt-1 text-[10.5px] leading-relaxed text-stone-500">Resend or replace it from a terminal&apos;s Control tab.</p>
                </div>
              ) : (issued.provisionCodes[0] || codesFromOrder[0]) && (
                <div className="rounded-lg border border-stone-200 bg-white p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-400">Device code</p>
                  <p className="mt-1 font-mono text-[19px] font-bold tracking-[0.14em] text-stone-900">{(issued.provisionCodes[0] || codesFromOrder[0]).code}</p>
                </div>
              )}
              <div className="rounded-lg border border-stone-200 bg-white p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-400">Owner PIN</p>
                <p className="mt-1 font-mono text-[19px] font-bold tracking-[0.14em] text-stone-900">
                  {issued.pin || <span className="text-[13px] font-normal tracking-normal text-stone-400">not recoverable</span>}
                </p>
                {/* Said plainly: the operator decides now whether to print it. */}
                <p className="mt-1 text-[10.5px] leading-relaxed text-stone-500">
                  {issued.pinViewableLater
                    ? 'The owner can look this up again in their own settings.'
                    : 'Not recoverable from here — print or note it now.'}
                </p>
              </div>
            </div>
            {/* Never box a till that has not cached its lock: it would boot to PIN login, not Hello. */}
            {(issued.locked || orderLocked) && orderId && <LockReceiptList orderId={orderId} />}
            {/* Stated, not assumed: the location grant is the half of this that
                is invisible on the card and in the email, so it is the half an
                operator would otherwise have to go and check by hand. */}
            {issued.locationAccess && (
              <p className="mt-3 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-emerald-800">
                <Check size={12} className="mt-0.5 shrink-0" />
                {issued.locationAccess.created ? 'Location access enabled' : 'Location access already on, refreshed'} — <strong>{issued.locationAccess.role}</strong> at ${issued.locationAccess.hourlyRate}/hr, on the till, the web app and online. The owner can change the rate.
              </p>
            )}
            {issued.locationAccess === null && (
              <p className="mt-3 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-amber-800">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                No location on this order, so no location access was granted — turn it on by hand before the merchant signs in.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="mr-auto">{lockSwitch}</span>
            <button
              onClick={() => setShowAccess(true)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-stone-300 bg-white px-3.5 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50"
            >
              <ShieldCheck size={13} /> Verify access
            </button>
            {issued.emailedTo && (
              <span className="inline-flex min-h-[44px] items-center gap-1.5 text-[11.5px] text-stone-500">
                <Mail size={12} /> sent
              </span>
            )}
            {/* The safe one, so it sits before the destructive one. Re-runs
                every step against the same PIN — for a second till on this
                order, or just to prove the setup still holds. */}
            <button
              onClick={() => run()}
              disabled={busy}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-stone-300 bg-white px-3.5 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />}
              Re-run setup
            </button>
            <button
              onClick={() => setReissuing(true)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-stone-300 bg-white px-3.5 text-[12.5px] font-semibold text-stone-700 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800"
            >
              <KeyRound size={13} /> Re-issue PIN
            </button>
            {!(lockFailed && !orderLocked) && <button
              onClick={printCard}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-rose-600 px-4 text-[12.5px] font-semibold text-white hover:bg-rose-700"
            >
              <Printer size={13} /> Print the card
            </button>}
          </div>
        </>
      )}

      {reissuing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setReissuing(false)}>
          <div className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-stone-200 px-5 py-4">
              <h3 className="text-[15px] font-bold text-stone-900">Issue a new PIN?</h3>
              <p className="mt-1 text-[12.5px] text-stone-500">{ownerEmail}</p>
            </div>
            <div className="px-5 py-4 text-[12.5px] leading-relaxed text-stone-600">
              {/* The thing an operator needs to know BEFORE clicking, because
                  the old PIN is very likely already printed and in a box. */}
              The current PIN stops working immediately. If the card has already been printed or
              the email sent, both become wrong — you will need to print and send again.
              <span className="mt-2 block text-stone-500">
                The role and the device code are untouched.
              </span>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
              <button
                onClick={() => setReissuing(false)}
                className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={() => { setReissuing(false); void run({ reissuePin: true }); }}
                className="min-h-[44px] rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700"
              >
                Issue a new PIN
              </button>
            </div>
          </div>
        </div>
      )}

      {showAccess && issued && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowAccess(false)}>
          <div className="max-h-[85vh] w-full max-w-[620px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-stone-200 px-5 py-4">
              <h3 className="text-[15px] font-bold text-stone-900">{issued.roleName}</h3>
              <p className="mt-1 text-[12.5px] text-stone-500">
                {ownerEmail} · {locationName || locationId}
                {issued.roleCreated ? ' · role created now' : ' · existing role reused'}
              </p>
            </div>
            <AccessVerifyTabs
              roleName={issued.roleName}
              posButtons={issued.resolvedPosButtons}
            />
            <div className="flex justify-end border-t border-stone-200 px-5 py-3.5">
              <button
                onClick={() => setShowAccess(false)}
                className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TillHandoverPanel;
